// ============================================
// Driver Jobs History Page
// Displays all completed/cancelled routes & jobs
// ============================================

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import DriverMobileHeader from '../../components/driver/DriverMobileHeader';
import { driverMenuItems } from './driverNav';
import { useTheme } from '../../context/ThemeContext';

const decodeHtml = (html) => {
    if (!html) return '';
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    // Handle double/triple encoded entities
    let decoded = txt.value;
    while (decoded.includes('&amp;')) {
        txt.innerHTML = decoded;
        decoded = txt.value;
    }
    return decoded;
};

const MissionHistory = () => {
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();

    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const res = await api.get('/driver/history');
                if (res.data.success) {
                    setHistory(res.data.data);
                }
            } catch (err) {
                console.error('Fetch mission history error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, []);

    const formatDate = (dateString) => {
        const d = new Date(dateString);
        return new Intl.DateTimeFormat('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        }).format(d);
    };

    return (
        <div className="flex flex-col sm:flex-row h-screen bg-slate-100 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative">
            <Sidebar items={driverMenuItems} theme="teal" title="IdaCab Driver" />

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Mobile Header */}
                <DriverMobileHeader user={user} theme={theme} toggleTheme={toggleTheme} />

                {/* Desktop Header */}
                <header className="hidden sm:flex h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl items-center px-10 flex-shrink-0 z-50 relative border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <h2 className="text-2xl font-black bg-gradient-to-r from-teal-600 to-teal-400 bg-clip-text text-transparent tracking-tighter">Jobs History</h2>
                </header>

                <main className="flex-1 overflow-y-auto px-4 pt-2 pb-28 sm:p-8 relative bg-transparent sidebar-scroll transition-colors duration-500">
                    <div className="max-w-4xl mx-auto">
                        {/* Page title — desktop only (mobile uses bottom nav for context) */}
                        <div className="hidden sm:block mb-8">
                            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2 uppercase">Past Jobs</h1>
                            <p className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Your completed and cancelled trips</p>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : history.length === 0 ? (
                            <div className="text-center py-20 bg-white/50 dark:bg-slate-900/40 backdrop-blur-xl rounded-[2.5rem] border border-dashed border-slate-300/50 dark:border-slate-700/50 shadow-sm">
                                <div className="w-20 h-20 rounded-full bg-white/50 dark:bg-slate-700/30 flex items-center justify-center mx-auto mb-6 opacity-50 shadow-inner border border-white/60 dark:border-white/5">
                                    <svg className="w-9 h-9 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                </div>
                                <h3 className="font-black text-slate-900 dark:text-white text-lg uppercase tracking-tight">No History Yet</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest mt-2">Your completed jobs will appear here</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {history.map((item) => (
                                    <div key={item._id} className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 p-6 shadow-xl shadow-slate-200/50 dark:shadow-black/50 hover:shadow-2xl hover:shadow-teal-500/15 dark:hover:shadow-teal-500/10 transition-all duration-500 group overflow-hidden">
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 relative z-10">
                                            
                                            {/* Icon Group */}
                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner shrink-0 ${
                                                item.type === 'route' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400'
                                            }`}>
                                                {item.type === 'route' ? (
                                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                                                ) : (
                                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10l-2.7-3.6A2 2 0 0013.7 5H6.3a2 2 0 00-1.6.9L2 9.5C1.4 9.8 1 10.4 1 11v5c0 .6.4 1 1 1h2" /><circle cx="7" cy="17" r="2" /><circle cx="17" cy="17" r="2" /></svg>
                                                )}
                                            </div>

                                            {/* Details */}
                                             <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-xl border text-[9px] font-black uppercase tracking-wider ${
                                                        item.type === 'route'
                                                            ? 'bg-indigo-50 dark:bg-slate-900/80 text-indigo-600 dark:text-indigo-400 border-indigo-500/30'
                                                            : 'bg-amber-50 dark:bg-slate-900/80 text-amber-600 dark:text-amber-400 border-amber-500/30'
                                                    }`}>
                                                        {item.type === 'route' ? 'School Route' : 'Private Job'}
                                                    </span>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                        {formatDate(item.date)}
                                                    </span>
                                                </div>
                                                <h4 className="text-base sm:text-lg font-black text-slate-900 dark:text-white uppercase leading-tight mt-1">{decodeHtml(item.name)}</h4>
                                                {item.destination && (
                                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-start gap-1.5 mt-1.5 leading-snug">
                                                        <svg className="w-3 h-3 text-teal-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                        <span>{decodeHtml(item.destination)}</span>
                                                    </p>
                                                )}
                                             </div>

                                            {/* Status Badge */}
                                             <div className="flex flex-col items-start sm:items-end gap-2 shrink-0 w-full sm:w-auto mt-4 sm:mt-0 pt-4 sm:pt-0 border-t sm:border-t-0 border-slate-100/80 dark:border-white/5">
                                                {item.status === 'cancelled' ? (
                                                    <div className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700/30 text-slate-600 dark:text-slate-400 text-xs font-black uppercase tracking-widest border border-slate-200 dark:border-white/10">
                                                        Cancelled
                                                    </div>
                                                ) : item.completionStatus === 'issue' ? (
                                                    <div className="px-4 py-2 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 text-xs font-black uppercase tracking-widest border border-rose-100 dark:border-rose-500/20 flex items-center gap-1.5">
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Issue Reported
                                                    </div>
                                                ) : (
                                                    <div className="px-4 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-black uppercase tracking-widest border border-emerald-100 dark:border-emerald-500/20 flex items-center gap-1.5">
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg> Successful
                                                    </div>
                                                )}
                                                
                                                {item.price > 0 && (
                                                    <div className="text-sm font-black text-teal-600 bg-teal-50 px-3 py-1 rounded-lg">
                                                        +£{item.price.toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Optional Note Expander */}
                                        {item.completionNote && (
                                            <div className="mt-4 pt-4 border-t border-slate-50 relative z-10">
                                                <div className="flex items-start gap-2">
                                                    <span className="text-slate-300 text-sm mt-0.5">📝</span>
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Driver Note</p>
                                                        <p className="text-sm font-medium text-slate-600 italic">"{item.completionNote}"</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </main>
            </div>
            
            <BottomNav items={driverMenuItems} />
        </div>
    );
};

export default MissionHistory;
